import React from 'react';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/formatters';
import { getDepartmentName, useDepartments } from '../../../hooks/useDepartments';

// Filter dates are plain `yyyy-MM-dd`: read and render them in UTC so the
// day doesn't shift with the browser's timezone.
const formatFilterDate = (yyyyMmDd, language) =>
  yyyyMmDd ? formatDate(`${yyyyMmDd}T00:00:00Z`, language, { timeZone: 'UTC', empty: yyyyMmDd }) : '';

const LeadsFilterChips = ({ filters, onChange, collaborators = [] }) => {
  const { t, i18n } = useTranslation();
  const { data: departments = [] } = useDepartments();

  if (!filters) return null;

  const chips = [];

  if (filters.status?.length) {
    const labels = filters.status.map((s) => t(`leads.filters.status_${s}`)).join(', ');
    chips.push({
      key: 'status',
      label: `${t('leads.filters.status')}: ${labels}`,
      onDelete: () => onChange({ ...filters, status: [] }),
    });
  }
  // Chip do colaborador
  if (filters.collaborator) {
    const found = collaborators.find((c) => c.uuid === filters.collaborator);
    const label = found ? (found.name || found.email || filters.collaborator) : filters.collaborator;
    chips.push({
      key: 'collaborator',
      label: `${t('leads.filters.collaborator')}: ${label}`,
      onDelete: () => onChange({ ...filters, collaborator: '' }),
    });
  }
  if (filters.department) {
    chips.push({
      key: 'department',
      label: `${t('leads.filters.department')}: ${getDepartmentName(departments, filters.department)}`,
      onDelete: () => onChange({ ...filters, department: '' }),
    });
  }
  if (filters.temperatureBuckets?.length) {
    const labels = filters.temperatureBuckets
      .map((b) => t(`leads.filters.temp_${b}`))
      .join(', ');
    chips.push({
      key: 'temperatureBuckets',
      label: `${t('leads.filters.temperature')}: ${labels}`,
      onDelete: () => onChange({ ...filters, temperatureBuckets: [] }),
    });
  }
  if (filters.assignedToMe) {
    chips.push({
      key: 'assignedToMe',
      label: t('leads.filters.assignedToMe'),
      onDelete: () => onChange({ ...filters, assignedToMe: false }),
    });
  }
  if (filters.waitingForReply) {
    chips.push({
      key: 'waitingForReply',
      label: t('leads.filters.waitingForReply'),
      onDelete: () => onChange({ ...filters, waitingForReply: false }),
    });
  }
  if (filters.createdAfter) {
    chips.push({
      key: 'createdAfter',
      label: `${t('leads.filters.createdAfter')}: ${formatFilterDate(filters.createdAfter, i18n.language)}`,
      onDelete: () => onChange({ ...filters, createdAfter: '' }),
    });
  }
  if (filters.createdBefore) {
    chips.push({
      key: 'createdBefore',
      label: `${t('leads.filters.createdBefore')}: ${formatFilterDate(filters.createdBefore, i18n.language)}`,
      onDelete: () => onChange({ ...filters, createdBefore: '' }),
    });
  }
  if (chips.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        mb: 1.5,
      }}
    >
      {chips.map((c) => (
        <Chip
          key={c.key}
          label={c.label}
          size="small"
          onDelete={c.onDelete}
          color="primary"
          variant="outlined"
        />
      ))}
    </Box>
  );
};

export default LeadsFilterChips;
